import React, { useState, useEffect, useCallback } from 'react';
import { Spinner, Alert, Modal, Button, Form } from 'react-bootstrap';
import {
  FaPlus, FaTrash, FaSync, FaShoppingCart, FaChevronDown, FaChevronRight,
  FaCheckCircle, FaClock, FaExclamationTriangle, FaExternalLinkAlt, FaEdit,
  FaFileInvoiceDollar, FaTruck
} from 'react-icons/fa';
import api from '../services/api';

/* ─── helpers ────────────────────────────────────────────────────────────── */
const fmt    = v => Number(v || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
const fmtN   = v => Number(v || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const fmtDate = d => d ? new Date(d).toLocaleDateString('fr-FR') : '—';

const BROWN = '#6d4e36';

const STATUT_CFG = {
  complet:    { color: '#27ae60', bg: '#edfaf1', label: 'Réceptionné',  icon: <FaCheckCircle /> },
  partiel:    { color: '#e67e22', bg: '#fef9ee', label: 'Partiel',       icon: <FaExclamationTriangle /> },
  en_attente: { color: '#1565c0', bg: '#eef4fc', label: 'En attente',    icon: <FaClock /> }
};

const PAIE_CFG = {
  paye:        { color: '#27ae60', bg: '#edfaf1', label: 'Payé',         icon: <FaCheckCircle /> },
  partiel:     { color: '#e67e22', bg: '#fef9ee', label: 'Partiel.paye', icon: <FaExclamationTriangle /> },
  non_paye:    { color: '#c0392b', bg: '#fdf0ee', label: 'à payer',     icon: <FaExclamationTriangle /> },
  non_facture: { color: '#888',    bg: '#f5f5f5', label: 'à facturer',  icon: <FaClock /> }
};

function PaieBadge({ statut }) {
  const cfg = PAIE_CFG[statut] || PAIE_CFG.non_facture;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 10px', borderRadius: 12,
      background: cfg.bg, color: cfg.color,
      fontWeight: 700, fontSize: '0.78rem', border: `1px solid ${cfg.color}`
    }}>
      {cfg.icon} {cfg.label}
    </span>
  );
}

function StatutBadge({ statut }) {
  const cfg = STATUT_CFG[statut] || STATUT_CFG.en_attente;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 10px', borderRadius: 12,
      background: cfg.bg, color: cfg.color,
      fontWeight: 700, fontSize: '0.78rem', border: `1px solid ${cfg.color}`
    }}>
      {cfg.icon} {cfg.label}
    </span>
  );
}

const th = { padding: '7px 10px', fontWeight: 600, fontSize: '0.78rem', borderBottom: '2px solid #e8d5c4', color: BROWN, background: '#faf5f0' };
const td = { padding: '6px 10px', color: '#444', verticalAlign: 'middle', fontSize: '0.83rem' };

/* ═══════════════════════════════════════════════════════════════════════════ */
export default function BdcSuivisPage({ user }) {
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  /* filtres */
  const [filtreStatut, setFiltreStatut] = useState('all');

  /* expanded lignes */
  const [expanded, setExpanded] = useState({});

  /* modal ajouter */
  const [showAdd,    setShowAdd]    = useState(false);
  const [addId,      setAddId]      = useState('');
  const [addNote,    setAddNote]    = useState('');
  const [addLoading, setAddLoading] = useState(false);
  const [addError,   setAddError]   = useState('');

  /* modal note */
  const [noteItem,    setNoteItem]    = useState(null);
  const [noteVal,     setNoteVal]     = useState('');
  const [noteSaving,  setNoteSaving]  = useState(false);

  /* ── chargement ── */
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/bdc-suivis');
      setItems(data.items || []);
    } catch (e) {
      setError(e.response?.data?.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  /* ── ajouter ── */
  const handleAdd = async () => {
    const id = parseInt(addId);
    if (!id) { setAddError('Entrez un ID de BDC valide.'); return; }
    setAddLoading(true); setAddError('');
    try {
      await api.post('/bdc-suivis', { IDBDC: id, note: addNote });
      setShowAdd(false); setAddId(''); setAddNote('');
      fetchData();
    } catch (e) {
      setAddError(e.response?.data?.message || 'Erreur');
    } finally {
      setAddLoading(false);
    }
  };

  /* ── supprimer ── */
  const handleDelete = async (item) => {
    if (!window.confirm(`Retirer le BDC #${item.IDBDC} du suivi ?`)) return;
    try {
      await api.delete(`/bdc-suivis/${item.suivi_id}`);
      fetchData();
    } catch (e) {
      alert(e.response?.data?.message || 'Erreur');
    }
  };

  /* ── note ── */
  const handleNoteSave = async () => {
    if (!noteItem) return;
    setNoteSaving(true);
    try {
      await api.put(`/bdc-suivis/${noteItem.suivi_id}/note`, { note: noteVal });
      setNoteItem(null);
      fetchData();
    } catch (e) {
      alert(e.response?.data?.message || 'Erreur');
    } finally {
      setNoteSaving(false);
    }
  };

  /* ── filtrage ── */
  const displayed = items.filter(i => filtreStatut === 'all' || i.statut === filtreStatut);

  /* ── KPIs ── */
  const nbComplet   = items.filter(i => i.statut === 'complet').length;
  const nbPartiel   = items.filter(i => i.statut === 'partiel').length;
  const nbAttente   = items.filter(i => i.statut === 'en_attente').length;
  const nbNonPaye   = items.filter(i => ['non_paye','partiel'].includes(i.statut_paiement)).length;
  const mtRestant   = items.reduce((s, i) => s + (parseFloat(i.montant_restant_ttc)  || 0), 0);
  const mtRestFac   = items.reduce((s, i) => s + (parseFloat(i.total_restant_fac)    || 0), 0);

  const kpis = [
    { key: 'all',       label: 'BDC suivis',       value: items.length,           color: BROWN,     bg: '#fdf6f0' },
    { key: 'en_attente',label: 'En attente',        value: nbAttente,              color: '#1565c0', bg: '#eef4fc' },
    { key: 'partiel',   label: 'Partiels',          value: nbPartiel,              color: '#e67e22', bg: '#fef9ee' },
    { key: 'complet',   label: 'Réceptionnés',      value: nbComplet,              color: '#27ae60', bg: '#edfaf1' },
    { key: '_restant',  label: 'Restant à livrer',  value: fmtN(mtRestant) + ' MAD', color: BROWN,  bg: '#f5f0eb', noFilter: true },
    { key: '_impaye',   label: 'BDC non réglés',    value: nbNonPaye,              color: '#c0392b', bg: '#fdf0ee', noFilter: true },
    { key: '_restfac',  label: 'Reste à payer',     value: fmtN(mtRestFac) + ' MAD', color: '#c0392b', bg: '#fdf0ee', noFilter: true },
  ];

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 16px', fontFamily: 'sans-serif' }}>

      {/* ── En-tête ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 24 }}>
        <div>
          <h4 style={{ color: BROWN, fontWeight: 700, margin: 0 }}>Suivi des BDC</h4>
          <p style={{ color: '#888', margin: 0, fontSize: '0.85rem' }}>Bons de commande à suivre — réception et état d'avancement</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={fetchData} disabled={loading}
            style={{ background: 'none', border: '1px solid #ddd', borderRadius: 6, padding: '6px 10px', cursor: 'pointer', color: BROWN }}
          >
            {loading
              ? <Spinner animation="border" size="sm" style={{ width: 14, height: 14, borderWidth: 2 }} />
              : <FaSync />}
          </button>
          <button
            onClick={() => { setAddId(''); setAddNote(''); setAddError(''); setShowAdd(true); }}
            style={{ background: BROWN, color: '#fff', border: 'none', borderRadius: 8, padding: '7px 16px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <FaPlus /> Ajouter un BDC
          </button>
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {/* ── KPI cards ── */}
      {items.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10, marginBottom: 20 }}>
          {kpis.map(k => {
            const active = !k.noFilter && filtreStatut === k.key;
            return (
              <div key={k.key}
                onClick={() => !k.noFilter && setFiltreStatut(k.key)}
                style={{
                  background: k.bg, borderRadius: 10, padding: '12px 16px',
                  border: `1px solid ${active ? k.color : k.color + '22'}`,
                  borderLeft: `3px solid ${k.color}`,
                  cursor: k.noFilter ? 'default' : 'pointer',
                  boxShadow: active ? `0 0 0 2px ${k.color}55` : 'none',
                  transform: active ? 'translateY(-1px)' : 'none',
                  transition: 'all .15s'
                }}
              >
                <div style={{ fontSize: '1.5rem', fontWeight: 800, color: k.color, lineHeight: 1 }}>{k.value}</div>
                <div style={{ fontSize: '0.72rem', color: k.color, marginTop: 3, fontWeight: 600 }}>{k.label}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Bouton reset filtre */}
      {filtreStatut !== 'all' && (
        <button onClick={() => setFiltreStatut('all')} style={{
          marginBottom: 14, padding: '5px 14px', borderRadius: 8, fontSize: '0.82rem', fontWeight: 600,
          border: '1px solid #ddd', background: '#fff', color: BROWN, cursor: 'pointer'
        }}>✕ Réinitialiser filtre</button>
      )}

      {/* ── Vide ── */}
      {!loading && items.length === 0 && (
        <div style={{ textAlign: 'center', padding: 60, color: '#bbb', border: '2px dashed #e8d5c4', borderRadius: 12 }}>
          <FaShoppingCart size={36} style={{ marginBottom: 12 }} />
          <p>Aucun BDC suivi. Cliquez sur <b>+ Ajouter un BDC</b> pour commencer.</p>
        </div>
      )}

      {/* ── Liste ── */}
      {displayed.map(item => {
        const cfg    = STATUT_CFG[item.statut] || STATUT_CFG.en_attente;
        const paieCfg= PAIE_CFG[item.statut_paiement] || PAIE_CFG.non_facture;
        const isOpen = expanded[item.suivi_id];
        const pct    = item.total_cmd > 0 ? Math.round((item.total_rec / item.total_cmd) * 100) : 0;
        const pctPaye= item.total_facture > 0 ? Math.round((item.total_paye / item.total_facture) * 100) : 0;

        return (
          <div key={item.suivi_id} style={{ border: '1px solid #e8d5c4', borderLeft: `4px solid ${cfg.color}`, borderRadius: 10, marginBottom: 14, overflow: 'hidden' }}>

            {/* ── Header ── */}
            <div style={{ padding: '14px 18px', background: cfg.bg, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
              <div style={{ flex: 1 }}>
                {/* Chantier */}
                <div style={{ fontSize: '0.75rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 3 }}>
                  {item.chantier_libelle || '—'}
                </div>

                {/* Titre + badges */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <a href={`https://cienco.ngrok.io/fiche_bon_de_commande/${item.IDBDC}`}
                    target="_blank" rel="noreferrer"
                    style={{ fontWeight: 800, fontSize: '1.05rem', color: BROWN, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 5 }}
                  >
                    BDC #{item.IDBDC} <FaExternalLinkAlt size={11} />
                  </a>
                  {item.REF && <span style={{ fontSize: '0.78rem', color: '#999', background: '#f5f5f5', borderRadius: 4, padding: '1px 7px' }}>{item.REF}</span>}
                  <StatutBadge statut={item.statut} />
                  <PaieBadge statut={item.statut_paiement} />
                </div>

                {/* Infos ligne */}
                <div style={{ marginTop: 4, fontSize: '0.8rem', color: '#888', display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                  <span>Fournisseur : <b style={{ color: '#555' }}>{item.fournisseur}</b></span>
                  <span>BDC du : <b style={{ color: '#555' }}>{fmtDate(item.DATE_BDC)}</b></span>
                  {item.date_premiere_reception && (
                    <span><FaTruck size={10} style={{ marginRight: 3 }} />
                      1ère réception : <b style={{ color: '#555' }}>{fmtDate(item.date_premiere_reception)}</b>
                      {item.nb_brs > 1 && item.date_derniere_reception !== item.date_premiere_reception &&
                        <> — dernière : <b style={{ color: '#555' }}>{fmtDate(item.date_derniere_reception)}</b> ({item.nb_brs} BRs)</>
                      }
                    </span>
                  )}
                </div>

                {/* Barre réception */}
                <div style={{ marginTop: 8, maxWidth: 380 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', color: '#aaa', marginBottom: 2 }}>
                    <span><FaTruck size={10} /> Réception : <b style={{ color: cfg.color }}>{pct}%</b></span>
                    <span>{fmtN(item.montant_restant_ttc)} MAD restants</span>
                  </div>
                  <div style={{ height: 7, borderRadius: 4, background: '#e8e8e8', overflow: 'hidden', marginBottom: 6 }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: cfg.color, borderRadius: 4, transition: 'width .4s' }} />
                  </div>

                  {/* Barre paiement */}
                  {item.total_facture > 0 && (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', color: '#aaa', marginBottom: 2 }}>
                        <span><FaFileInvoiceDollar size={10} /> Paiement : <b style={{ color: paieCfg.color }}>{pctPaye}%</b></span>
                        <span>{fmtN(item.total_restant_fac)} MAD à payers</span>
                      </div>
                      <div style={{ height: 7, borderRadius: 4, background: '#e8e8e8', overflow: 'hidden' }}>
                        <div style={{ width: `${pctPaye}%`, height: '100%', background: paieCfg.color, borderRadius: 4, transition: 'width .4s' }} />
                      </div>
                    </>
                  )}
                </div>

                {/* Note */}
                {item.note && (
                  <div style={{ marginTop: 6, fontSize: '0.77rem', color: '#aaa', fontStyle: 'italic' }}>
                    📝 {item.note}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                <button
                  onClick={() => { setNoteItem(item); setNoteVal(item.note || ''); }}
                  style={{ background: '#fff', border: `1px solid ${BROWN}`, borderRadius: 6, padding: '4px 10px', cursor: 'pointer', color: BROWN, fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 4 }}
                ><FaEdit /> Note</button>
                <button
                  onClick={() => setExpanded(p => ({ ...p, [item.suivi_id]: !p[item.suivi_id] }))}
                  style={{ background: '#fff', border: '1px solid #ddd', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', color: '#666', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 4 }}
                >
                  {isOpen ? <FaChevronDown size={11} /> : <FaChevronRight size={11} />}
                  Détails
                </button>
                <button
                  onClick={() => handleDelete(item)}
                  style={{ background: '#fff', border: '1px solid #e74c3c', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', color: '#e74c3c', fontSize: '0.8rem' }}
                  title="Retirer du suivi"
                ><FaTrash /></button>
              </div>
            </div>

            {/* ── Détail lignes + factures ── */}
            {isOpen && (
              <div style={{ borderTop: '1px solid #f0e8df' }}>

                {/* Lignes articles */}
                <div style={{ padding: '10px 18px 4px', fontSize: '0.78rem', fontWeight: 700, color: BROWN, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <FaTruck size={12} /> Lignes de commande
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={th}>Article</th>
                        <th style={{ ...th, textAlign: 'right' }}>Commandé</th>
                        <th style={{ ...th, textAlign: 'right' }}>Reçu</th>
                        <th style={{ ...th, textAlign: 'right' }}>Restant</th>
                        <th style={{ ...th, textAlign: 'right' }}>Montant restant TTC</th>
                      </tr>
                    </thead>
                    <tbody>
                      {item.lines.map((l, i) => {
                        const restant = parseFloat(l.qte_restante) || 0;
                        const lineColor = restant === 0 ? '#27ae60' : parseFloat(l.qte_recue) > 0 ? '#e67e22' : '#1565c0';
                        return (
                          <tr key={l.line_id || i} style={{ borderBottom: '1px solid #f5efe8' }}>
                            <td style={td}>
                              <div style={{ fontWeight: 600, color: '#333' }}>{l.article_libelle || `#${l.IDARTICLE}`}</div>
                              {l.code_produit && <div style={{ fontSize: '0.72rem', color: '#aaa' }}>{l.code_produit}</div>}
                            </td>
                            <td style={{ ...td, textAlign: 'right' }}>{fmt(l.qte_commandee)} {l.unite}</td>
                            <td style={{ ...td, textAlign: 'right', color: '#27ae60', fontWeight: 600 }}>{fmt(l.qte_recue)} {l.unite}</td>
                            <td style={{ ...td, textAlign: 'right', color: lineColor, fontWeight: 700 }}>{fmt(restant)} {l.unite}</td>
                            <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{fmtN(l.montant_restant_ttc)} MAD</td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr style={{ background: '#faf5f0', fontWeight: 700 }}>
                        <td colSpan={4} style={{ ...td, textAlign: 'right', color: BROWN }}>Total restant réception :</td>
                        <td style={{ ...td, textAlign: 'right', color: BROWN }}>{fmtN(item.montant_restant_ttc)} MAD</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {/* Factures */}
                {item.factures.length > 0 && (
                  <>
                    <div style={{ padding: '10px 18px 4px', fontSize: '0.78rem', fontWeight: 700, color: '#8e44ad', display: 'flex', alignItems: 'center', gap: 6, borderTop: '1px solid #f0e8df' }}>
                      <FaFileInvoiceDollar size={12} /> Factures liées
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr>
                            <th style={{ ...th, color: '#8e44ad' }}>Facture</th>
                            <th style={{ ...th, color: '#8e44ad' }}>Date</th>
                            <th style={{ ...th, textAlign: 'right', color: '#8e44ad' }}>Total TTC</th>
                            <th style={{ ...th, textAlign: 'right', color: '#8e44ad' }}>Payé</th>
                            <th style={{ ...th, textAlign: 'right', color: '#8e44ad' }}>Restant</th>
                          </tr>
                        </thead>
                        <tbody>
                          {item.factures.map((f, i) => {
                            const restFac = parseFloat(f.total_ttc_left) || 0;
                            const payeFac = (parseFloat(f.total_ttc) || 0) - restFac;
                            return (
                              <tr key={f.IDFACTURE || i} style={{ borderBottom: '1px solid #f5efe8' }}>
                                <td style={td}>
                                  <a href={`https://cienco.ngrok.io/fiche_facture/${f.IDFACTURE}`}
                                    target="_blank" rel="noreferrer"
                                    style={{ color: '#8e44ad', fontWeight: 600, textDecoration: 'none' }}
                                  >#{f.IDFACTURE}</a>
                                  {f.NFacture && <span style={{ marginLeft: 6, color: '#aaa', fontSize: '0.75rem' }}>{f.NFacture}</span>}
                                </td>
                                <td style={td}>{fmtDate(f.date_fac)}</td>
                                <td style={{ ...td, textAlign: 'right' }}>{fmtN(f.total_ttc)} MAD</td>
                                <td style={{ ...td, textAlign: 'right', color: '#27ae60', fontWeight: 600 }}>{fmtN(payeFac)} MAD</td>
                                <td style={{ ...td, textAlign: 'right', color: restFac > 0 ? '#c0392b' : '#27ae60', fontWeight: 700 }}>{fmtN(restFac)} MAD</td>
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot>
                          <tr style={{ background: '#faf5f0', fontWeight: 700 }}>
                            <td colSpan={4} style={{ ...td, textAlign: 'right', color: '#8e44ad' }}>Total à payer :</td>
                            <td style={{ ...td, textAlign: 'right', color: item.total_restant_fac > 0 ? '#c0392b' : '#27ae60' }}>
                              {fmtN(item.total_restant_fac)} MAD
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* ═══ MODAL : Ajouter un BDC ═══ */}
      <Modal show={showAdd} onHide={() => setShowAdd(false)} centered>
        <Modal.Header closeButton style={{ borderColor: '#f0e8df' }}>
          <Modal.Title style={{ fontSize: '1rem', color: BROWN, fontWeight: 700 }}>
            <FaShoppingCart style={{ marginRight: 8 }} /> Ajouter un BDC à suivre
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600 }}>ID du BDC</Form.Label>
            <input
              type="number"
              style={{ border: '1px solid #ddd', borderRadius: 6, padding: '8px 12px', fontSize: '1rem', width: '100%', outline: 'none', fontWeight: 700 }}
              placeholder="Ex: 1042"
              value={addId}
              onChange={e => setAddId(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAdd()}
              autoFocus
            />
          </Form.Group>
          <Form.Group>
            <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600 }}>Note (optionnel)</Form.Label>
            <input
              type="text"
              style={{ border: '1px solid #ddd', borderRadius: 6, padding: '7px 12px', fontSize: '0.9rem', width: '100%', outline: 'none' }}
              placeholder="Ex: Urgent — béton pour fondations"
              value={addNote}
              onChange={e => setAddNote(e.target.value)}
            />
          </Form.Group>
          {addError && <Alert variant="danger" className="mt-3" style={{ fontSize: '0.85rem' }}>{addError}</Alert>}
        </Modal.Body>
        <Modal.Footer style={{ borderColor: '#f0e8df' }}>
          <Button variant="secondary" size="sm" onClick={() => setShowAdd(false)}>Annuler</Button>
          <Button size="sm" onClick={handleAdd} disabled={addLoading || !addId}
            style={{ background: BROWN, border: 'none', fontWeight: 600 }}>
            {addLoading ? <Spinner size="sm" animation="border" /> : 'Ajouter'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ═══ MODAL : Note ═══ */}
      <Modal show={!!noteItem} onHide={() => setNoteItem(null)} centered size="sm">
        <Modal.Header closeButton style={{ borderColor: '#f0e8df' }}>
          <Modal.Title style={{ fontSize: '1rem', color: BROWN, fontWeight: 700 }}>
            Note — BDC #{noteItem?.IDBDC}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <input
            type="text"
            style={{ border: '1px solid #ddd', borderRadius: 6, padding: '8px 12px', fontSize: '0.9rem', width: '100%', outline: 'none' }}
            placeholder="Ex: Urgent — béton pour fondations"
            value={noteVal}
            onChange={e => setNoteVal(e.target.value)}
            autoFocus
          />
        </Modal.Body>
        <Modal.Footer style={{ borderColor: '#f0e8df' }}>
          <Button variant="secondary" size="sm" onClick={() => setNoteItem(null)}>Annuler</Button>
          <Button size="sm" onClick={handleNoteSave} disabled={noteSaving}
            style={{ background: BROWN, border: 'none', fontWeight: 600 }}>
            {noteSaving ? <Spinner size="sm" animation="border" /> : 'Enregistrer'}
          </Button>
        </Modal.Footer>
      </Modal>

    </div>
  );
}
